import React, { useEffect, useRef, useState } from "react";
import { IonPage, IonContent, IonButton, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const heavyEmojis = ["🐘", "🏋️", "🚚", "🚂", "🏠", "⛰️", "🚀", "🛳️", "🏆", "🦣", "🦛"];
const lightEmojis = ["🍃", "🦋", "🎈", "🕊️", "🪶", "☁️", "🌻", "🧸", "🪁", "🍂", "🥄"];

const HeavyLightGame: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState<string[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Heavy and Light Worksheets for UKG | Weight Comparison Activities";
    }, []);

    const generateQuestion = () => {
        const heavy = heavyEmojis[Math.floor(Math.random() * heavyEmojis.length)];
        const light = lightEmojis[Math.floor(Math.random() * lightEmojis.length)];
        const isHeavyQuestion = Math.random() < 0.5;

        setQuestion(isHeavyQuestion ? "Which one is heavier?" : "Which one is lighter?");
        setOptions(Math.random() < 0.5 ? [heavy, light] : [light, heavy]);
        setCorrectAnswer(isHeavyQuestion ? heavy : light);
        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG students understand the concept of heavy and light through fun comparisons, real-life examples, and printable worksheets. Practice early weight recognition today!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/heavy-light" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord heavyLight matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Heavy and Light</h1>

                                    <p>
                                        Learning the difference between <strong>heavy</strong> and <strong>light</strong> is a key part of early childhood math. It helps children observe the world around them, develop comparison skills, and begin to understand the concept of weight. These <strong>UKG heavy and light worksheets</strong> make the process simple, visual, and fun.
                                    </p>

                                    <h2>⚖️ What Do Heavy and Light Mean?</h2>
                                    <p>
                                        “Heavy“ means something has more weight. “Light“ means it has less weight. Children can feel the difference by lifting objects and observing how their body reacts.
                                        <ul>
                                            <li>A watermelon is <strong>heavy</strong>, but an apple is <strong>light</strong>.</li>
                                            <li>A backpack full of books is <strong>heavier</strong> than an empty bag.</li>
                                        </ul>
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify objects that are <strong>heavy or light</strong></li>
                                        <li>Compare two or more items by weight (visually or physically)</li>
                                        <li>Develop vocabulary: <strong>heavier, lighter</strong></li>
                                        <li>Build observation and decision-making skills</li>
                                    </ul>

                                    <h2>🖍️ Activities in the Worksheets</h2>
                                    <ul>
                                        <li>Match heavy and light objects</li>
                                        <li>Tick the heavier object in a pair</li>
                                        <li>Circle the lightest object among three</li>
                                        <li>Use balance scale illustrations to predict weight</li>
                                        <li>Color or draw real-world examples of heavy and light items</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Use household objects (<strong>e.g., ball vs. pillow</strong>) to demonstrate weight</li>
                                        <li>Let children lift and compare objects under supervision</li>
                                        <li>Discuss weight naturally during daily routines (e.g., carrying groceries)</li>
                                        <li>Use visual aids like a weighing scale or balance toy</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Let your child carry two bags—one full, one empty—and ask which is heavier. This hands-on activity builds real understanding beyond just pictures.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Heavy and Light</h1>

                                <p>
                                    Learning the difference between <strong>heavy</strong> and <strong>light</strong> is a key part of early childhood math. It helps children observe the world around them, develop comparison skills, and begin to understand the concept of weight. These <strong>UKG heavy and light worksheets</strong> make the process simple, visual, and fun.
                                </p>

                                <h2>⚖️ What Do Heavy and Light Mean?</h2>
                                <p>
                                    “Heavy“ means something has more weight. “Light“ means it has less weight. Children can feel the difference by lifting objects and observing how their body reacts.
                                    <ul>
                                        <li>A watermelon is <strong>heavy</strong>, but an apple is <strong>light</strong>.</li>
                                        <li>A backpack full of books is <strong>heavier</strong> than an empty bag.</li>
                                    </ul>
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify objects that are <strong>heavy or light</strong></li>
                                    <li>Compare two or more items by weight (visually or physically)</li>
                                    <li>Develop vocabulary: <strong>heavier, lighter</strong></li>
                                    <li>Build observation and decision-making skills</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Match heavy and light objects</li>
                                    <li>Tick the heavier object in a pair</li>
                                    <li>Circle the lightest object among three</li>
                                    <li>Use balance scale illustrations to predict weight</li>
                                    <li>Color or draw real-world examples of heavy and light items</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use household objects (<strong>e.g., ball vs. pillow</strong>) to demonstrate weight</li>
                                    <li>Let children lift and compare objects under supervision</li>
                                    <li>Discuss weight naturally during daily routines (e.g., carrying groceries)</li>
                                    <li>Use visual aids like a weighing scale or balance toy</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Let your child carry two bags—one full, one empty—and ask which is heavier. This hands-on activity builds real understanding beyond just pictures.
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="optionImageDiv" style={{ display: "flex", justifyContent: "center", gap: "30px" }}>
                                {options.map((emoji, index) => (
                                    <div key={index} onClick={() => checkAnswer(emoji)}>
                                        {emoji}
                                    </div>
                                ))}
                            </div>
                            <p style={{ fontSize: "1.5rem" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default HeavyLightGame;
